import React, { useState, useEffect } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  type CompetitorItem,
  addCompetitor,
  updateCompetitor
} from '../services/apiService';
import { CompetitorCategory } from '../types/competitor';

interface CompetitorModalProps {
  isOpen: boolean;
  onClose: () => void;
  competitor: CompetitorItem | null;
  onSuccess: () => void;
  setSuccess: (success: string | null) => void;
}

const CompetitorModal: React.FC<CompetitorModalProps> = ({
  isOpen,
  onClose,
  competitor,
  onSuccess,
  setSuccess
}) => {
  const [formData, setFormData] = useState<Partial<CompetitorItem>>({
    name: '',
    talking_points: '',
    category: 'direct_competitor',
    advantages: [],
    weaknesses: [],
    isActive: true
  });

  const [saving, setSaving] = useState(false);
  const [newAdvantage, setNewAdvantage] = useState('');
  const [newWeakness, setNewWeakness] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const modalContentRef = React.useRef<HTMLDivElement>(null);

  // Scroll to top when error is set
  useEffect(() => {
    if (localError && modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [localError]);

  useEffect(() => {
    if (isOpen) {
      // Reset all state when modal opens
      setNewAdvantage('');
      setNewWeakness('');
      setLocalError(null);

      if (competitor) {
        setFormData({
          name: competitor.name,
          talking_points: competitor.talking_points,
          category: competitor.category,
          advantages: competitor.advantages || [],
          weaknesses: competitor.weaknesses || [],
          isActive: competitor.isActive
        });
      } else {
        setFormData({
          name: '',
          talking_points: '',
          category: 'direct_competitor',
          advantages: [],
          weaknesses: [],
          isActive: true
        });
      }
    }
  }, [isOpen, competitor]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!formData.name?.trim()) {
      setLocalError('Competitor name is required');
      return;
    }

    // Validate that at least 1 advantage and 1 weakness are added
    if (!formData.advantages || formData.advantages.length === 0) {
      setLocalError('Please add at least 1 advantage over this competitor');
      return;
    }

    if (!formData.weaknesses || formData.weaknesses.length === 0) {
      setLocalError('Please add at least 1 weakness of this competitor');
      return;
    }

    try {
      setSaving(true);
      console.log('Submitting competitor data:', formData);
      if (competitor) {
        await updateCompetitor(competitor._id, formData);
        setSuccess('Competitor updated successfully');
        onSuccess();
        onClose();
      } else {
        const result = await addCompetitor(formData);
        console.log('Competitor created:', result);
        setSuccess('Competitor added successfully');
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Error saving competitor:', error);

      // Handle duplicate key error specifically
      // Check both error.message and error.error for E11000 or duplicate
      const errorText = error?.error || error?.message || '';
      if (errorText.includes('E11000') ||
          errorText.includes('duplicate key') ||
          errorText.includes('already exists')) {
        setLocalError('A competitor with this name already exists. Please use a different name.');
      } else {
        // Show the actual error message if available, otherwise generic message
        setLocalError(error?.message || error?.error || 'Failed to save competitor');
      }
    } finally {
      setSaving(false);
    }
  };

  const addAdvantage = () => {
    console.log('addAdvantage function called! newAdvantage value:', newAdvantage);
    if (newAdvantage.trim()) {
      const newAdvantages = [...(formData.advantages || []), { point: newAdvantage.trim(), category: CompetitorCategory.OTHER }];
      console.log('Adding advantage, new advantages:', newAdvantages);
      setFormData({
        ...formData,
        advantages: newAdvantages
      });
      setNewAdvantage('');
    } else {
      console.log('Advantage is empty or whitespace only');
    }
  };

  const removeAdvantage = (index: number) => {
    setFormData({
      ...formData,
      advantages: formData.advantages?.filter((_, i) => i !== index) || []
    });
  };

  const addWeakness = () => {
    console.log('addWeakness function called! newWeakness value:', newWeakness);
    if (newWeakness.trim()) {
      const newWeaknesses = [...(formData.weaknesses || []), { point: newWeakness.trim(), category: CompetitorCategory.OTHER }];
      console.log('Adding weakness, new weaknesses:', newWeaknesses);
      setFormData({
        ...formData,
        weaknesses: newWeaknesses
      });
      setNewWeakness('');
    } else {
      console.log('Weakness is empty or whitespace only');
    }
  };

  const removeWeakness = (index: number) => {
    setFormData({
      ...formData,
      weaknesses: formData.weaknesses?.filter((_, i) => i !== index) || []
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300] overflow-y-auto">
      <div ref={modalContentRef} className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">
            {competitor ? 'Edit Competitor' : 'Add Competitor'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Display */}
          {localError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-900 mb-1">Error</h3>
                  <p className="text-sm text-red-800">{localError}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setLocalError(null)}
                  className="text-red-600 hover:text-red-800"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Competitor Name *
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={!!competitor}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  competitor ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                required
              />
              {competitor && (
                <p className="text-xs text-gray-500 mt-1">
                  Name cannot be changed. Delete and recreate if needed.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={formData.category || 'direct_competitor'}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="direct_competitor">Direct Competitor</option>
                <option value="indirect_competitor">Indirect Competitor</option>
                <option value="alternative_solution">Alternative Solution</option>
              </select>
            </div>
          </div>

          {/* Talking Points */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Talking Points
            </label>
            <textarea
              value={formData.talking_points || ''}
              onChange={(e) => setFormData({ ...formData, talking_points: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Key points to mention when this competitor is detected..."
            />
          </div>

          {/* Advantages */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Our Advantages *
            </label>
            <p className="text-xs text-gray-500 mb-2">At least 1 advantage is required. Press Enter or click Add.</p>
            <div className="space-y-2">
              {formData.advantages?.map((advantage, index) => (
                <div key={index} className="flex items-center gap-2 bg-green-50 p-2 rounded-md">
                  <span className="flex-1 text-sm">{advantage.point}</span>
                  <button
                    type="button"
                    onClick={() => removeAdvantage(index)}
                    className="px-2 py-1 text-red-600 hover:bg-red-100 rounded-md text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newAdvantage}
                  onChange={(e) => setNewAdvantage(e.target.value)}
                  onKeyDown={(e) => {
                    console.log('Key pressed in advantage input:', e.key, 'Current value:', newAdvantage);
                    if (e.key === 'Enter') {
                      console.log('Enter key detected! Preventing default and calling addAdvantage...');
                      e.preventDefault();
                      e.stopPropagation();
                      addAdvantage();
                    }
                  }}
                  placeholder="Add an advantage over this competitor... (Press Enter)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    console.log('Add advantage button clicked!');
                    e.preventDefault();
                    addAdvantage();
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Weaknesses */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Their Weaknesses *
            </label>
            <p className="text-xs text-gray-500 mb-2">At least 1 weakness is required. Press Enter or click Add.</p>
            <div className="space-y-2">
              {formData.weaknesses?.map((weakness, index) => (
                <div key={index} className="flex items-center gap-2 bg-red-50 p-2 rounded-md">
                  <span className="flex-1 text-sm">{weakness.point}</span>
                  <button
                    type="button"
                    onClick={() => removeWeakness(index)}
                    className="px-2 py-1 text-red-600 hover:bg-red-100 rounded-md text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newWeakness}
                  onChange={(e) => setNewWeakness(e.target.value)}
                  onKeyDown={(e) => {
                    console.log('Key pressed in weakness input:', e.key, 'Current value:', newWeakness);
                    if (e.key === 'Enter') {
                      console.log('Enter key detected! Preventing default and calling addWeakness...');
                      e.preventDefault();
                      e.stopPropagation();
                      addWeakness();
                    }
                  }}
                  placeholder="Add a weakness of this competitor... (Press Enter)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    console.log('Add weakness button clicked!');
                    e.preventDefault();
                    addWeakness();
                  }}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive !== false}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
              Active (Enable detection for this competitor)
            </label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {competitor ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>{competitor ? 'Update' : 'Create'} Competitor</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompetitorModal;